'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TrainingResource {
  id: number;
  title: string;
  category: string;
  duration: string;
  format: string;
  description: string;
  icon: string;
  downloadSize?: string;
}

interface SafetyTrainingResourcesProps {
  className?: string;
}

const SafetyTrainingResources = ({ className = '' }: SafetyTrainingResourcesProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = ['All', 'Driver Training', 'Safety Protocols', 'Emergency Response', 'Compliance'];

  const trainingResources: TrainingResource[] = [
    {
      id: 1,
      title: "Defensive Driving Techniques",
      category: "Driver Training",
      duration: "45 minutes",
      format: "Video Course",
      description: "Comprehensive training on defensive driving strategies for African road conditions, including hazard recognition and accident prevention.",
      icon: "TruckIcon",
      downloadSize: "250 MB"
    },
    {
      id: 2,
      title: "Cargo Securing Best Practices",
      category: "Safety Protocols",
      duration: "30 minutes",
      format: "Interactive Guide",
      description: "Step-by-step instructions for proper cargo loading, weight distribution, and securing techniques for various cargo types.",
      icon: "CubeIcon",
      downloadSize: "15 MB"
    },
    {
      id: 3,
      title: "Emergency Response Procedures",
      category: "Emergency Response",
      duration: "60 minutes",
      format: "Video + Manual",
      description: "Detailed protocols for handling accidents, breakdowns, security threats, and medical emergencies during transport operations.",
      icon: "ExclamationTriangleIcon",
      downloadSize: "180 MB"
    },
    {
      id: 4,
      title: "First Aid & Medical Response",
      category: "Emergency Response",
      duration: "90 minutes",
      format: "Certification Course",
      description: "Certified first aid training covering common injuries, medical emergencies, and proper use of emergency medical equipment.",
      icon: "HeartIcon",
      downloadSize: "320 MB"
    },
    {
      id: 5,
      title: "Customs Compliance Documentation",
      category: "Compliance",
      duration: "40 minutes",
      format: "PDF Guide",
      description: "Complete guide to customs documentation requirements, border procedures, and regulatory compliance across African countries.",
      icon: "DocumentCheckIcon",
      downloadSize: "8 MB"
    },
    {
      id: 6,
      title: "Vehicle Pre-Trip Inspection",
      category: "Safety Protocols",
      duration: "25 minutes",
      format: "Checklist + Video",
      description: "Mandatory pre-trip inspection procedures ensuring vehicle safety and compliance before every journey.",
      icon: "WrenchScrewdriverIcon",
      downloadSize: "95 MB"
    },
    {
      id: 7,
      title: "Security Awareness Training",
      category: "Safety Protocols",
      duration: "50 minutes",
      format: "Interactive Course",
      description: "Comprehensive security training covering threat recognition, cargo protection, and personal safety in high-risk areas.",
      icon: "ShieldCheckIcon",
      downloadSize: "210 MB"
    },
    {
      id: 8,
      title: "Environmental Protection Guidelines",
      category: "Compliance",
      duration: "35 minutes",
      format: "Video Course",
      description: "Environmental compliance training covering waste management, spill prevention, and sustainable logistics practices.",
      icon: "GlobeAltIcon",
      downloadSize: "140 MB"
    },
    {
      id: 9,
      title: "Fatigue Management & Rest Breaks",
      category: "Driver Training",
      duration: "30 minutes",
      format: "Interactive Module",
      description: "Training on recognizing fatigue symptoms, mandatory rest periods, and strategies for maintaining alertness during long hauls.",
      icon: "ClockIcon",
      downloadSize: "75 MB"
    }
  ];

  const filteredResources = selectedCategory === 'All'
    ? trainingResources
    : trainingResources.filter(resource => resource.category === selectedCategory);

  if (!isHydrated) {
    return (
      <section className={`py-16 lg:py-24 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-8 bg-border rounded w-1/3 mx-auto" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-64 bg-border rounded" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="AcademicCapIcon" size={20} className="text-primary" />
              <span className="font-body font-semibold text-sm text-primary">Training & Education</span>
            </div>
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
              Safety Training Resources
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive training materials ensuring all personnel maintain the highest safety and compliance standards.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2.5 rounded-lg font-body font-semibold text-sm transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-accent to-secondary text-primary shadow-md'
                    : 'bg-card text-muted-foreground border border-border hover:border-accent hover:text-foreground'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {filteredResources.map((resource) => (
              <div
                key={resource.id}
                className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-primary/5 to-accent/5 p-6 border-b border-border">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon name={resource.icon as any} size={24} className="text-accent" />
                  </div>
                  <h3 className="font-headline font-bold text-lg text-foreground mb-2">
                    {resource.title}
                  </h3>
                  <span className="inline-block px-3 py-1 bg-accent/10 rounded-full text-xs font-body font-semibold text-accent">
                    {resource.category}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="font-body text-sm text-muted-foreground mb-4 leading-relaxed">
                    {resource.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-body text-muted-foreground">Duration:</span>
                      <span className="font-body font-semibold text-foreground">{resource.duration}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-body text-muted-foreground">Format:</span>
                      <span className="font-body font-semibold text-foreground">{resource.format}</span>
                    </div>
                    {resource.downloadSize && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-body text-muted-foreground">Size:</span>
                        <span className="font-body font-semibold text-foreground">{resource.downloadSize}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <button className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-accent/10 to-secondary/10 border border-accent/20 rounded-lg font-body font-semibold text-sm text-accent hover:from-accent hover:to-secondary hover:text-primary transition-all duration-300 group-hover:shadow-md">
                    <Icon name="ArrowDownTrayIcon" size={16} />
                    <span>Download Resource</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Training Statistics */}
          <div className="bg-card rounded-xl border border-border p-8 lg:p-12">
            <h3 className="font-headline font-bold text-2xl text-center text-foreground mb-8">
              Training Program Impact
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '2,500+', label: 'Personnel Trained', icon: 'UserGroupIcon' },
                { value: '50+', label: 'Training Modules', icon: 'BookOpenIcon' },
                { value: '98%', label: 'Completion Rate', icon: 'CheckBadgeIcon' },
                { value: '40hrs', label: 'Annual Training/Driver', icon: 'ClockIcon' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <Icon name={stat.icon as any} size={32} className="text-accent mx-auto mb-3" />
                  <div className="font-headline font-bold text-3xl text-foreground mb-1">
                    {stat.value}
                  </div>
                  <div className="font-body text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Access Information */}
          <div className="mt-8 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl border border-primary/20 p-6 lg:p-8">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center flex-shrink-0">
                <Icon name="InformationCircleIcon" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-headline font-bold text-lg text-foreground mb-2">
                  Access Training Resources
                </h4>
                <p className="font-body text-sm text-muted-foreground leading-relaxed mb-4">
                  All training materials are available to MylesDrive personnel through our secure learning management system. External partners and clients can request access to relevant training modules by contacting our training department.
                </p>
                <button className="inline-flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-accent to-secondary text-primary font-body font-semibold text-sm rounded-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                  <Icon name="EnvelopeIcon" size={16} />
                  <span>Request Training Access</span>
                  <Icon name="ArrowRightIcon" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SafetyTrainingResources;